/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import firebaseApp from "@src/auth/lib/firebaseClient";
import { Auth, User, getAuth } from "firebase/auth";
import {
  CourieUser,
  GetCourierQuery,
  SyncUserWithFirebaseMutation,
  useGetCourierLazyQuery,
  UserRoleType,
  useSyncUserWithFirebaseMutation,
} from "@api/graphql/generated/generated-types";
import { useRouter } from "next/router";
import { useLocalStorage } from "usehooks-ts";
import { FetchResult } from "@apollo/client";
import { Loading } from "@src/common/components";
import moment from "moment";
import commonRoutes from "@src/common/lib/routing/commonRoutes";
import { useErrorToast } from "@src/common/hooks/useErrorToast";

interface AuthContextInterface {
  authLoading: boolean;
  isAuthenticated: boolean;
  courier: GetCourierQuery | undefined;
  courierId: string | null;
  setCourierId: (courierId: string) => void;
  refetchCourier: () => void;
  employeeId: string | null;
  courieUser: CourieUser | null;
  isRoleType: (roleType: UserRoleType) => boolean;
  hasPermission: (permission: string) => boolean;
}

export const AuthContext = createContext<AuthContextInterface>({
  authLoading: true,
  isAuthenticated: false,
  courier: undefined,
  courierId: null,
  setCourierId: () => {},
  refetchCourier: () => {},
  courieUser: null,
  employeeId: null,
  isRoleType: (roleType) => false,
  hasPermission: () => false,
});

interface Props {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<Props> = ({ children }: Props) => {
  const router = useRouter();
  const showError = useErrorToast();

  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [courieUser, setCourieUser] = useState<CourieUser | null>(null);

  // Storing courierId in local storage so that it persists on page refresh
  // Not necessary for user as we are using firebase auth for that
  // but needed for employee login to store in local.
  const [courierId, setCourierId] = useLocalStorage<string | null>("courierId", null);

  const [getCourier, { data: courier, refetch: refetchCourier }] = useGetCourierLazyQuery({
    onError: (error) => showError(error),
    fetchPolicy: "network-only",
  });

  const [syncUserWithFirebaseMutation] = useSyncUserWithFirebaseMutation({
    onError: (error) => showError(error),
  });

  useEffect(() => {
    const auth: Auth = getAuth(firebaseApp);

    const unsubscribe = auth.onAuthStateChanged((authUser) => {
      setAuthLoading(true);
      if (!authUser) {
        router.push("/welcome/login").then(() => {
          resetAllStates();
        });
      } else {
        setFirebaseUser(authUser);
      }
    });

    return () => unsubscribe();
  }, []);

  const resetAllStates = () => {
    setAuthLoading(false);
    setFirebaseUser(null);
    setCourierId(null);
    setCourieUser(null);
    setEmployeeId(null);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    if (!firebaseUser && !authLoading) {
      router.push("/welcome/login").then(() => {
        setIsAuthenticated(false);
      });
    }

    firebaseUser?.getIdTokenResult().then((token) => {
      syncUserWithFirebaseMutation({
        variables: {
          email: firebaseUser.email!,
          firebaseUid: firebaseUser.uid,
        },
      }).then(async (res: FetchResult<SyncUserWithFirebaseMutation>) => {
        if (res.data?.syncUserWithFirebase) {
          const { user, error, employeeId } = res.data.syncUserWithFirebase;

          // we need to synchronously request the courier now
          // otherwise, pages will render with incorrect timezone
          if (courierId || user?.courierId) {
            await getCourier({
              variables: {
                courierId: user?.courierId || courierId!,
              }
            });
          }

          if (!employeeId) {
            setCourierId(user?.courierId ?? null);
          }

          setEmployeeId(employeeId ?? null);
          setCourieUser(user ?? null);
          setIsAuthenticated(true);

          // employees on /internal/ pages don't need a courier selected
          if (employeeId && !courierId && router.pathname.startsWith("/internal/")) {
            setAuthLoading(false);
          // employees need to select a courier before accessing the main app
          } else if (employeeId && !courierId) {
            router.push(commonRoutes.dashboard).then(() => {
              setAuthLoading(false);
            });
          // missing user or error means new user
          } else if (error && !user) {
            router.push(commonRoutes.newUser).then(() => {
              setAuthLoading(false);
            });
          // otherwise remove loading state  
          } else {
            setAuthLoading(false);
          }
        }
      });
    });
  }, [firebaseUser]);

  useEffect(() => {
    if (courierId && firebaseUser) {
      // refresh if the courierId is different
      if (courier?.courier?.id !== courierId) {
        getCourier({
          variables: {
            courierId: courierId,
          }
        });
      }
    }
  }, [courierId, firebaseUser]);

  useEffect(() => {
    if (courier?.courier?.timeZoneId) {
      moment.tz.setDefault(courier.courier.timeZoneId);
    }
  }, [courier]);

  const isRoleType = useCallback((roleType: UserRoleType) => {
    if (employeeId) return true;
    if (!courieUser) return false

    // Custom role permission is only configured in the backend. In the frontend, we will always return true.
    if (courieUser.roleType === UserRoleType.Custom) {
      return true;
    }
    switch (roleType) {
      case UserRoleType.Csr: return [UserRoleType.Administrator, UserRoleType.Dispatcher, UserRoleType.Csr].includes(courieUser.roleType)
      case UserRoleType.Dispatcher: return [UserRoleType.Administrator, UserRoleType.Dispatcher].includes(courieUser.roleType)
      case UserRoleType.Administrator: return [UserRoleType.Administrator].includes(courieUser.roleType)
      default: return false;
    }
  }, [employeeId, courieUser]);

  const hasPermission = useCallback((permission: string): boolean => {
    if (employeeId) return true;
    if (!courieUser) return false;
    return courieUser.computedPermissions.includes(permission);
  }, [employeeId, courieUser]);

  const contextValue = useMemo(
    () => ({
      authLoading,
      isAuthenticated,
      courier,
      courierId,
      setCourierId,
      refetchCourier,
      courieUser,
      employeeId,
      isRoleType,
      hasPermission,
    }),
    [authLoading, isAuthenticated, courier, courierId, setCourierId, refetchCourier, courieUser, employeeId, isRoleType, hasPermission]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      <Loading loading={authLoading}>{children}</Loading>
    </AuthContext.Provider>
  );
};
